<div id="login-bg" class="flex flex-col justify-between items-center min-h-screen p-4 sm:p-6 lg:p-8">
  <!-- Central Card -->
  <main class="w-full max-w-md my-auto animate-slide-up">
    <div class="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl">
      <!-- Header with Logo, Title and Subtitle in Juboo brand colors -->
      <div class="flex flex-col items-center text-center mb-6">
        <div class="mb-4 flex items-center justify-center">
          <img src="assets/logo-juboo.png" alt="Juboo" class="h-12 sm:h-14 w-auto object-contain" />
        </div>
        <h1 class="text-2xl sm:text-3xl font-extrabold text-primary tracking-tight">
          {{ 'RESET_PASSWORD.TITLE' | translate }}
        </h1>
        <p class="text-xs sm:text-sm text-stone-500 mt-1.5 max-w-xs font-normal">
          {{ 'RESET_PASSWORD.SUBTITLE' | translate }}
        </p>
      </div>

      <!-- Success Alert -->
      @if (successMessage) {
        <div class="mb-4 py-2.5 px-3.5 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium rounded-lg flex items-center gap-2 animate-fade-in shadow-xs">
          <svg class="w-4 h-4 shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span class="flex-1">{{ successMessage }}</span>
        </div>
      }

      <!-- Error Alert -->
      @if (error) {
        <div class="mb-4 py-2.5 px-3.5 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-medium rounded-lg flex items-center gap-2 animate-fade-in shadow-xs">
          <svg class="w-4 h-4 shrink-0 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="flex-1 font-semibold">{{ error }}</span>
        </div>
      }

      <!-- Missing Key Warning Fallback -->
      @if (!key) {
        <div class="text-center py-4 space-y-4">
          <p class="text-sm text-stone-600">
            {{ 'RESET_PASSWORD.ERRORS.MISSING_KEY' | translate }}
          </p>
          <div class="flex justify-center">
            <app-button
              variant="primary"
              size="md"
              routerLink="/forgot-password"
              customClass="px-6 shadow-xs"
            >
              {{ 'RESET_PASSWORD.REQUEST_NEW_LINK' | translate }}
            </app-button>
          </div>
        </div>
      }

      @if (showButtonConfirme && key) {
        <form [formGroup]="resetPasswordForm" (submit)="onSubmit($event)">
          <app-input
            id="password"
            type="password"
            icon="lock"
            [label]="'RESET_PASSWORD.LABELS.PASSWORD' | translate"
            formControlName="password"
            placeholder="••••••••••"
            autocomplete="new-password"
            [error]="(resetPasswordForm.get('password')?.invalid && resetPasswordForm.get('password')?.touched) ? ('RESET_PASSWORD.ERRORS.INCORRECT_PASSWORD' | translate) : null"
          ></app-input>

          <!-- Password Requirements Checklist -->
          @if (passwordValue.length > 0) {
            <div class="mb-3 p-3 bg-stone-50 rounded-xl border border-stone-200/80 text-xs space-y-1.5 animate-fade-in">
              <p class="font-semibold text-stone-700 mb-1">
                {{ 'RESET_PASSWORD.CRITERIA.TITLE' | translate }}
              </p>
              <div class="flex items-center gap-2" [class.text-emerald-700]="hasMinLength" [class.text-stone-400]="!hasMinLength">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  @if (hasMinLength) {
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  } @else {
                    <circle cx="10" cy="10" r="4" />
                  }
                </svg>
                <span>{{ 'RESET_PASSWORD.CRITERIA.MIN_LENGTH' | translate }}</span>
              </div>
              <div class="flex items-center gap-2" [class.text-emerald-700]="hasUpperCase" [class.text-stone-400]="!hasUpperCase">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  @if (hasUpperCase) {
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  } @else {
                    <circle cx="10" cy="10" r="4" />
                  }
                </svg>
                <span>{{ 'RESET_PASSWORD.CRITERIA.UPPERCASE' | translate }}</span>
              </div>
              <div class="flex items-center gap-2" [class.text-emerald-700]="hasLowerCase" [class.text-stone-400]="!hasLowerCase">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  @if (hasLowerCase) {
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  } @else {
                    <circle cx="10" cy="10" r="4" />
                  }
                </svg>
                <span>{{ 'RESET_PASSWORD.CRITERIA.LOWERCASE' | translate }}</span>
              </div>
              <div class="flex items-center gap-2" [class.text-emerald-700]="hasSpecialChar" [class.text-stone-400]="!hasSpecialChar">
                <svg class="w-3.5 h-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  @if (hasSpecialChar) {
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                  } @else {
                    <circle cx="10" cy="10" r="4" />
                  }
                </svg>
                <span>{{ 'RESET_PASSWORD.CRITERIA.SPECIAL' | translate }}</span>
              </div>
            </div>
          }

          <app-input
            id="password2"
            type="password"
            icon="lock"
            [label]="'RESET_PASSWORD.LABELS.CONFIRM_PASSWORD' | translate"
            formControlName="password2"
            placeholder="••••••••••"
            autocomplete="new-password"
            [error]="(resetPasswordForm.get('password2')?.invalid && resetPasswordForm.get('password2')?.touched) ? ('RESET_PASSWORD.ERRORS.INCORRECT_PASSWORD' | translate) : null"
          ></app-input>

          <!-- Submit Button (Centered) -->
          <div class="mt-4 flex justify-center">
            <app-button
              type="submit"
              variant="primary"
              size="md"
              [loading]="loading"
              [disabled]="loading"
              customClass="px-8 shadow-xs"
            >
              <span class="leading-none">{{ 'RESET_PASSWORD.BUTTONS.CONFIRM' | translate }}</span>
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </app-button>
          </div>
        </form>
      }

      <!-- Back to Login Link -->
      <div class="mt-6 text-center">
        <a
          routerLink="/login"
          class="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:text-primary-700 transition"
        >
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          {{ 'RESET_PASSWORD.BACK_TO_LOGIN' | translate }}
        </a>
      </div>
    </div>
  </main>

  <!-- Footer -->
  <footer class="w-full text-center py-4 text-xs text-white/70 font-medium select-none">
    <div class="flex items-center justify-center gap-2">
      <svg class="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
      <span>Plateforme sécurisée &bull; Juboo &copy; {{ currentYear }}</span>
    </div>
  </footer>
</div>
